import styled from 'styled-components';
import { fontFamilies } from '~/theme/typography';

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${({ theme }) => theme.zIndex.modal};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: 0;
  pointer-events: auto;
`;

export const Modal = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
  max-width: 600px;
  max-height: calc(100vh - 48px);
  overflow: auto;
  background-color: ${({ theme }) => theme.colors.colorMain};
  border: 1px solid ${({ theme }) => theme.colors.white};
  border-radius: 1rem;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 0;
  opacity: 0;
`;

export const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
`;

export const HeaderText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-right: 40px;
`;

export const Title = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.white};
  font-family: ${fontFamilies.PantonRustHeavy};
  font-size: 24px;
  font-style: normal;
  font-weight: 100;
  line-height: normal;
  letter-spacing: 0.12px;
  text-transform: uppercase;

  @media ${({ theme }) => theme.breakpoints.sm} {
    font-size: 20px;
    letter-spacing: 0.1px;
  }
`;

export const Subtitle = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.white};
  font-family: ${fontFamilies.CircularStd};
  font-size: 16px;
  font-style: normal;
  font-weight: 450;
  line-height: normal;
  letter-spacing: 0.15px;
  text-transform: uppercase;
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 24px;
  right: 24px;
  width: 24px;
  height: 24px;
  padding: 0;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  & svg {
    display: block;
    width: 24px;
    height: 24px;
  }

  &:hover {
    opacity: 0.85;
  }

  @media ${({ theme }) => theme.breakpoints.sm} {
    top: 8px;
    right: 8px;
  }
`;

export const CategoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-width: 0;
  margin-top: 40px;

  @media ${({ theme }) => theme.breakpoints.sm} {
    margin-top: 24px;
  }
`;

export const CategoryItem = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 24px;
  align-items: start;
  min-width: 0;
`;

export const CategoryTextColumn = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  cursor: default;
  overflow-wrap: break-word;
`;

export const AccordionTrigger = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  text-align: left;
  cursor: default;
  font: inherit;
  color: inherit;

  @media ${({ theme }) => theme.breakpoints.sm} {
    cursor: pointer;
  }
`;

export const CategoryToggleColumn = styled.div`
  flex-shrink: 0;
  min-width: 44px;
`;

export const CategoryTitleRow = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

export const AccordionIcon = styled.span<{ $expanded: boolean }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: ${({ theme }) => theme.colors.white};

  & svg {
    display: block;
    width: 24px;
    height: 24px;
  }

  @media (min-width: 601px) {
    display: none;
  }
`;

export const CategoryTitle = styled.span`
  color: ${({ theme }) => theme.colors.white};
  font-family: ${fontFamilies.CircularStd};
  font-size: 18px;
  font-style: normal;
  font-weight: 900;
  line-height: normal;
  letter-spacing: 0.15px;
  text-transform: uppercase;
`;

export const AccordionContent = styled.div<{ $expanded: boolean }>`
  display: grid;
  grid-template-rows: 1fr;
  min-height: 0;

  @media ${({ theme }) => theme.breakpoints.sm} {
    grid-template-rows: ${({ $expanded }) => ($expanded ? '1fr' : '0fr')};
    transition: grid-template-rows 0.3s ease;
  }
`;

export const AccordionContentInner = styled.div`
  min-height: 0;
  overflow: hidden;
`;

export const CategoryDescription = styled.p`
  margin: 0;
  padding: 0;
  color: ${({ theme }) => theme.colors.white};
  font-family: ${fontFamilies.CircularStd};
  font-size: 14px;
  font-style: normal;
  font-weight: 450;
  line-height: normal;
  letter-spacing: 0.15px;
  text-transform: uppercase;
`;

export const ReloadNotice = styled.p`
  margin: 24px 0 0;
  padding: 0;
  color: ${({ theme }) => theme.colors.white};
  font-family: ${fontFamilies.CircularStd};
  font-size: 12px;
  font-style: normal;
  font-weight: 450;
  line-height: normal;
  letter-spacing: 0.15px;
  text-transform: uppercase;
  text-align: center;
  opacity: 0.7;

  @media ${({ theme }) => theme.breakpoints.sm} {
    margin-top: 16px;
    text-align: left;
  }
`;

export const SaveButton = styled.button`
  margin-top: 40px;
  padding: 12px;
  min-height: 42px;
  width: fit-content;
  align-self: center;
  border: none;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.colorMain};
  text-align: center;
  font-family: ${fontFamilies.CircularStd};
  font-size: 16px;
  font-style: normal;
  font-weight: 450;
  line-height: normal;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  @media ${({ theme }) => theme.breakpoints.sm} {
    margin-top: 24px;
    width: 100%;
    align-self: stretch;
  }
`;
